import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { RegistrationList } from '../../compositions/registrations/list/registration-list';
import { RegistrationModal } from '../../compositions/registrations/modals/registration-modal/registration-modal';
import { useAppContext } from '../../data/context/app-context';
import { useOpenRegistrationCount } from '../../data/data/registrations/use-open-registration-count';
import { useRegistrationItems } from '../../data/data/registrations/use-registration-items';
import {
  RegistrationItemDataFragment,
  RegistrationPersonItemDataFragment,
  RegistrationStatus,
} from '../../data/types/api/graphql';
import { useFormatters } from '../../data/utils/formatters.utils';
import { useNavigateWithHealthProvider } from '../../data/utils/router.utils';
import { Content } from '../../elements/content/content';
import { CustomFormattedMessage } from '../../elements/intl/custom-formatted-message';
import { DataTableLocationState } from '../../elements/table/data-table/data-table';
import { Tabs } from '../../elements/tabs/tabs';

type RegistrationsFilter = RegistrationStatus | 'ALL';

/**
 * Review screen for registrations, per practice, inside the Patients page: the
 * same staff that sees patients reviews them. The open ones are what the
 * practice has to act on, so that is the tab you land on.
 */
export function RegistrationsPage() {
  const { healthProviderId } = useAppContext();

  const { getFullName } = useFormatters();

  const { registrationId } = useParams();

  const navigate = useNavigateWithHealthProvider();

  const [status, setStatus] = useState<RegistrationsFilter>(
    RegistrationStatus.Open
  );

  const { data: registrationItemsData, loading: registrationItemsLoading } =
    useRegistrationItems(
      healthProviderId,
      status === 'ALL' ? null : (status as RegistrationStatus)
    );

  const { data: openRegistrationCountData } =
    useOpenRegistrationCount(healthProviderId);

  const onHandleStatus = useCallback((value: RegistrationsFilter) => {
    setStatus(value);
  }, []);

  const onHandleRegistration = useCallback(
    (value: RegistrationItemDataFragment) => {
      navigate(`/patients/registrations/${value.id}`);
    },
    [navigate]
  );

  /**
   * Accepting turns every person into a patient, so the detail can hand the
   * practice straight over to the patient list, pre-filtered on the name.
   */
  const onHandlePatient = useCallback(
    (value: RegistrationPersonItemDataFragment) => {
      navigate('/patients/all', {
        state: { query: getFullName(value) } as DataTableLocationState,
      });
    },
    [getFullName, navigate]
  );

  return (
    <>
      <Content
        toolbar={
          <Tabs<RegistrationsFilter>
            testId="registrations-status"
            value={status}
            items={[
              {
                key: RegistrationStatus.Open,
                title: (
                  <CustomFormattedMessage id="registrations.status.open" />
                ),
                count: openRegistrationCountData,
              },
              {
                key: RegistrationStatus.Accepted,
                title: (
                  <CustomFormattedMessage id="registrations.status.accepted" />
                ),
              },
              {
                key: RegistrationStatus.Rejected,
                title: (
                  <CustomFormattedMessage id="registrations.status.rejected" />
                ),
              },
              {
                key: 'ALL',
                title: <CustomFormattedMessage id="registrations.status.all" />,
              },
            ]}
            onChange={onHandleStatus}
          />
        }
      >
        <RegistrationList
          loading={registrationItemsLoading}
          items={registrationItemsData}
          onClick={onHandleRegistration}
        />
      </Content>

      {registrationId && (
        <RegistrationModal
          registrationId={registrationId}
          onPatient={onHandlePatient}
          onClose={() => navigate('/patients/registrations')}
        />
      )}
    </>
  );
}
